import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import { Column } from '@repo/ui/data-table'
import { BookmarkIcon, PlusIcon, PrintIcon, UploadIcon } from '@repo/ui/icons'
import { endOfYear, startOfYear } from 'date-fns'
import React, { useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { emailValidityStatusEnumMapping } from '../../lib/types/enums/information-requests/email-validity-status.enum'
import { origineEnumMapping } from '../../lib/types/enums/information-requests/origin.enum'
import {
  ReceiveBrochureEnum,
  receiveBrochureEnumMapping
} from '../../lib/types/enums/information-requests/recieve-brochure.enum'
import {
  InformationRequest,
  InformationRequestFilters
} from '../../lib/types/models/information-request/information-request'
import {
  transformInformationRequestFiltersToUrl,
  transformUrlToInformationRequestFilters
} from '../../lib/utils/filters-mapping/information-requests'
import InformationRequestFilter from './Information-request-filter'

export const informationRequestsInitialFilter: InformationRequestFilters = {
  origins: [],
  pdfTypes: [],
  createdAt: {
    startDate: DateFormatter.toDateFormatted(startOfYear(new Date())),
    endDate: DateFormatter.toDateFormatted(endOfYear(new Date()))
  },
  howDidYouHearAboutUsIds: [],
  receiveBrochureBy: [ReceiveBrochureEnum.EMAIL_AND_COURIER],
  isPrinted: false,
  inFrance: true
}

const validatePrintSelection = (
  rows: InformationRequest[],
  expectedType: ReceiveBrochureEnum
): InformationRequest[] => {
  const validRows = rows.filter((row) => row.receiveBrochureBy === expectedType)
  const invalidRows = rows.filter((row) => row.receiveBrochureBy !== expectedType)

  if (invalidRows.length === 0) return validRows

  const expectedTypeLabel = receiveBrochureEnumMapping[expectedType].toLowerCase()

  if (rows.length === 1) {
    toast.error(
      `Cette demande de brochure doit être de type "${expectedTypeLabel}" pour être imprimée.`
    )
    return []
  }

  if (validRows.length === 0) {
    toast.error(`Aucune des demandes sélectionnées n'est de type "${expectedTypeLabel}".`)
    return []
  }

  toast.error(
    `Certaines demandes de brochure sélectionnées ne sont pas de type "${expectedTypeLabel}" et ne seront pas imprimées.`
  )
  return validRows
}

const InformationRequestsListPage = () => {
  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<InformationRequestFilters>({
      initialFilters: informationRequestsInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformInformationRequestFiltersToUrl,
      transformUrlToFilter: transformUrlToInformationRequestFilters
    })

  const tableRef = React.useRef<DynamicTableRef<InformationRequest>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { downloadDocuments } = useDownloadDocuments()

  const { navigate, navigateWithEvent } = useNavigator()

  const { modalState, toggleModal, closeModel } = useModalManager<'filters'>(['filters'])

  const columns: Column<InformationRequest>[] = [
    {
      accessorKey: 'createdAt',
      header: 'Date de la demande',
      enableSorting: true,
      cell: (data) => DateFormatter.DDMMYYYY(data.row.original.createdAt)
    },
    {
      accessorKey: 'origin',
      header: 'Origine',
      enableSorting: true,
      cell: (data) => (
        <Badge
          text={origineEnumMapping[data.row.original.origin].label}
          variant={origineEnumMapping[data.row.original.origin].variant}
        />
      )
    },
    {
      accessorKey: 'parentLastName',
      header: 'NOM du parent',
      enableSorting: true
    },
    {
      accessorKey: 'parentFirstName',
      header: 'Prénom du parent',
      enableSorting: true
    },
    {
      accessorKey: 'childLastName',
      header: 'NOM jeune',
      enableSorting: true
    },
    {
      accessorKey: 'childFirstName',
      header: 'Prénom jeune',
      enableSorting: true
    },
    {
      accessorKey: 'phoneNumber',
      header: 'Tél.',
      enableSorting: true,
      cell: (data) => data.row.original.phoneNumber ?? ' - '
    },
    {
      accessorKey: 'postalCode',
      header: 'Adresse',
      enableSorting: true,
      cell: (data) => {
        const postalCode = data.row.original.postalCode
        const city = data.row.original.city
        if (!postalCode && !city) return ' - '
        return `${postalCode ?? ''} ${city?.toUpperCase() ?? ''}`
      }
    },
    {
      accessorKey: 'country',
      header: 'Pays',
      enableSorting: true,
      cell: (data) => data.row.original.country ?? ' - '
    },
    {
      accessorKey: 'email',
      header: 'Email',
      enableSorting: true
    },
    {
      accessorKey: 'emailValidityStatus',
      header: 'Email valide',
      enableSorting: true,
      cell: (data) => (
        <Badge
          text={emailValidityStatusEnumMapping[data.row.original.emailValidityStatus].label}
          variant={emailValidityStatusEnumMapping[data.row.original.emailValidityStatus].variant}
        />
      )
    },
    {
      accessorKey: 'printedAt',
      header: 'Impression étiquette',
      enableSorting: true,
      cell: (data) => {
        if (!data.row.original.printedAt) return ' - '
        return DateFormatter.DDMMYYYY(data.row.original.printedAt)
      }
    }
  ]

  return (
    <div>
      <UserNavBar title="Liste de demandes de brochures" icon={BookmarkIcon} />
      <div className="h-4" />
      <DynamicDataTable<InformationRequest, InformationRequestFilters>
        ref={tableRef}
        title="Toutes les demandes de brochures"
        onLoadingChange={setIsTableLoading}
        deleteMessage={{
          name: 'demande de brochure',
          attributeName: 'id',
          deleteOne: 'Êtes-vous sûr de vouloir supprimer cette demande de brochure ?',
          deleteMany: 'Êtes-vous sûr de vouloir supprimer ces demandes de brochures ?',
          deleteAll: 'Êtes-vous sûr de vouloir supprimer toutes les demandes de brochures ?'
        }}
        columns={columns}
        selectable
        allowSearchParam
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        endpoint={API.informationRequests.getAll.url}
        onFiltersClick={() => toggleModal('filters')}
        onRowClick={(row, event) =>
          navigateWithEvent(
            Paths.INFORMATION_REQUEST_UPDATE_PAGE.replace(':id', row.id.toString()),
            event
          )
        }
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Ajouter une demande',
            fn: () => navigate(Paths.INFORMATION_REQUEST_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: PrintIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Imprimer Catalogue 4',
            fn: async (rows) => {
              const validRows = validatePrintSelection(rows, ReceiveBrochureEnum.EMAIL_AND_COURIER)
              if (validRows.length === 0) return

              const ids = validRows.map((row) => row.id)
              await downloadDocuments({
                ids,
                endpoint: API.informationRequests.printPdfDestineo.url
              })
              tableRef.current?.refetch()
            }
          },
          {
            type: 'multiple',
            icon: PrintIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Imprimer Standard',
            fn: async (rows) => {
              const validRows = validatePrintSelection(rows, ReceiveBrochureEnum.EMAIL_AND_COURIER)
              if (validRows.length === 0) return

              const ids = validRows.map((row) => row.id)
              await downloadDocuments({
                ids,
                endpoint: API.informationRequests.printPdfStandard.url
              })
              tableRef.current?.refetch()
            }
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter',
            fn: async (rows) => {
              const ids = rows.map((row) => row.id)
              await downloadDocuments({ ids, endpoint: API.informationRequests.exportExcel.url })
            }
          }
        ]}
      />
      {/* Filters Modal */}
      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <InformationRequestFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}

export default InformationRequestsListPage
